"use client";

import * as React from "react";
import { Moon, Sun } from "lucide-react";
import { Button } from "../primitives/button";
import { cn } from "../primitives/cn";

const STORAGE_KEY = "lwp.theme";

export type ThemeMode = "light" | "dark";

function resolveSystemTheme(): ThemeMode {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyTheme(mode: ThemeMode) {
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(mode);
  root.style.colorScheme = mode;
}

/**
 * ThemeToggle — light / dark. Defaults to system preference until the Staff/Client
 * explicitly toggles (persisted in localStorage). Matches layout v3 topbar control.
 */
export function ThemeToggle({
  ariaLabelLight,
  ariaLabelDark,
  className,
}: {
  ariaLabelLight: string;
  ariaLabelDark: string;
  className?: string;
}) {
  const [mode, setMode] = React.useState<ThemeMode>("light");
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    const initial =
      stored === "light" || stored === "dark" ? stored : resolveSystemTheme();
    applyTheme(initial);
    setMode(initial);
    setReady(true);

    if (stored === "light" || stored === "dark") return;

    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const next = mq.matches ? "dark" : "light";
      applyTheme(next);
      setMode(next);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const toggle = () => {
    const next: ThemeMode = mode === "dark" ? "light" : "dark";
    window.localStorage.setItem(STORAGE_KEY, next);
    applyTheme(next);
    setMode(next);
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className={cn("size-8 shrink-0 text-muted-foreground", className)}
      onClick={toggle}
      aria-label={mode === "dark" ? ariaLabelLight : ariaLabelDark}
      title={mode === "dark" ? ariaLabelLight : ariaLabelDark}
      disabled={!ready}
    >
      {mode === "dark" ? (
        <Moon className="size-4" aria-hidden />
      ) : (
        <Sun className="size-4" aria-hidden />
      )}
    </Button>
  );
}
